import { useMemo, useState } from 'react'
import { Field, NumberInput, ResultStat, Select, Slider } from './ui'

// ─── Junction K-factor look-up (Crane TP-410 basis) ─────────────────────────
// K = friction_factor_coefficient × (L/D equivalent) per fitting
// Values represent total resistance coefficient for fully turbulent flow.
type JunctionKey = 'tee_through' | 'tee_branch' | 'y45_through' | 'y45_branch'

const JUNCTIONS: { label: string; value: JunctionKey; kFactor: number; note: string }[] = [
  {
    label: 'Tee — Straight Through',
    value: 'tee_through',
    kFactor: 0.3,
    note: 'K = 0.3 (Crane TP-410 Table A-29, straight-through run)',
  },
  {
    label: 'Tee — Branch Flow',
    value: 'tee_branch',
    kFactor: 1.0,
    note: 'K = 1.0 (Crane TP-410, flow diverted 90° into branch)',
  },
  {
    label: 'Y-Junction 45° — Through',
    value: 'y45_through',
    kFactor: 0.15,
    note: 'K = 0.15 (45° Wye, reduced turbulence in straight run)',
  },
  {
    label: 'Y-Junction 45° — Branch',
    value: 'y45_branch',
    kFactor: 0.5,
    note: 'K = 0.50 (45° Wye branch leg, Crane TP-410 basis)',
  },
]

// ─── Pipe schedule nominal bore table (mm → m) ──────────────────────────────
type PipeSize = '15' | '20' | '25' | '40' | '50' | '80' | '100' | '150'

const PIPE_ID_MM: Record<PipeSize, number> = {
  '15': 15.8,   // DN15  / ½"  Sch-40
  '20': 20.9,   // DN20  / ¾"
  '25': 26.6,   // DN25  / 1"
  '40': 40.9,   // DN40  / 1½"
  '50': 52.5,   // DN50  / 2"
  '80': 78.1,   // DN80  / 3"
  '100': 102.3, // DN100 / 4"
  '150': 154.1, // DN150 / 6"
}

const PIPE_OPTIONS = [
  { label: 'DN15 / ½" Sch-40  (ID 15.8 mm)', value: '15' as PipeSize },
  { label: 'DN20 / ¾" Sch-40  (ID 20.9 mm)', value: '20' as PipeSize },
  { label: 'DN25 / 1"  Sch-40  (ID 26.6 mm)', value: '25' as PipeSize },
  { label: 'DN40 / 1½" Sch-40  (ID 40.9 mm)', value: '40' as PipeSize },
  { label: 'DN50 / 2"  Sch-40  (ID 52.5 mm)', value: '50' as PipeSize },
  { label: 'DN80 / 3"  Sch-40  (ID 78.1 mm)', value: '80' as PipeSize },
  { label: 'DN100 / 4" Sch-40  (ID 102.3 mm)', value: '100' as PipeSize },
  { label: 'DN150 / 6" Sch-40  (ID 154.1 mm)', value: '150' as PipeSize },
]

// ─── Fluid presets ────────────────────────────────────────────────────────────
type FluidKey = 'water20' | 'water60' | 'oil_light' | 'oil_heavy' | 'air'

const FLUIDS: { label: string; value: FluidKey; rho: number; nu: number }[] = [
  { label: 'Water @ 20°C',        value: 'water20',   rho: 998.2,  nu: 1.004e-6 },
  { label: 'Water @ 60°C',        value: 'water60',   rho: 983.2,  nu: 0.474e-6 },
  { label: 'Light Oil (SAE 10)',   value: 'oil_light', rho: 870,    nu: 20e-6    },
  { label: 'Heavy Oil (SAE 40)',   value: 'oil_heavy', rho: 885,    nu: 100e-6   },
  { label: 'Air @ 20°C, 1 atm',   value: 'air',       rho: 1.204,  nu: 15.11e-6 },
]

const FLUID_OPTIONS = FLUIDS.map((f) => ({ label: f.label, value: f.value }))

const JUNCTION_OPTIONS = JUNCTIONS.map((j) => ({ label: j.label, value: j.value }))

// ─── Pipe roughness (Sch-40 carbon steel, mm) ────────────────────────────────
const ROUGHNESS_MM = 0.046 // commercial steel

function frictionFactor(Re: number, D_m: number): number {
  // Laminar
  if (Re < 2300) return 64 / Re
  // Colebrook-White (explicit Swamee-Jain approximation, valid Re 5e3–1e8)
  const eps = ROUGHNESS_MM / 1000
  const relRough = eps / D_m
  const f = 0.25 / Math.pow(Math.log10(relRough / 3.7 + 5.74 / Math.pow(Re, 0.9)), 2)
  return f
}

function formatPa(pa: number): string {
  if (pa >= 1e6) return `${(pa / 1e6).toFixed(3)} MPa`
  if (pa >= 1000) return `${(pa / 1000).toFixed(2)} kPa`
  return `${pa.toFixed(1)} Pa`
}

function formatBar(pa: number): string {
  return `${(pa / 1e5).toFixed(4)} bar`
}

function formatPsi(pa: number): string {
  return `${(pa / 6894.76).toFixed(3)} psi`
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function PipingFlowCalc() {
  const [pipeSize, setPipeSize]     = useState<PipeSize>('50')
  const [fluidKey, setFluidKey]     = useState<FluidKey>('water20')
  const [junction, setJunction]     = useState<JunctionKey>('tee_branch')
  const [velocity, setVelocity]     = useState(2.0)   // m/s
  const [pipeLen, setPipeLen]       = useState(10)     // m straight run
  const [numJunctions, setNumJunctions] = useState(1)

  const result = useMemo(() => {
    const D_mm = PIPE_ID_MM[pipeSize]
    const D_m  = D_mm / 1000
    const A    = Math.PI * (D_m / 2) ** 2       // pipe cross-section m²

    const fluid = FLUIDS.find((f) => f.value === fluidKey)!
    const rho   = fluid.rho   // kg/m³
    const nu    = fluid.nu    // m²/s

    const Re    = (velocity * D_m) / nu
    const f     = frictionFactor(Re, D_m)

    // Darcy-Weisbach: ΔP_pipe = f × (L/D) × (ρv²/2)
    const dynPress   = 0.5 * rho * velocity * velocity   // Pa
    const dpStraight = f * (pipeLen / D_m) * dynPress     // Pa

    // K-factor loss at junction(s)
    const jData = JUNCTIONS.find((j) => j.value === junction)!
    const K     = jData.kFactor
    const dpJunctions = numJunctions * K * dynPress        // Pa

    const dpTotal = dpStraight + dpJunctions

    // Flow rate
    const Q_m3s  = A * velocity      // m³/s
    const Q_lpm  = Q_m3s * 60000     // L/min
    const Q_gpm  = Q_m3s * 15850.3   // US gal/min

    // Equivalent length of junctions
    const Le     = (K * D_m) / f     // metres, equivalent pipe length per junction

    const regime =
      Re < 2300 ? 'Laminar'
      : Re < 4000 ? 'Transitional'
      : 'Turbulent'

    return {
      D_mm, D_m, Re, f, regime,
      dynPress, dpStraight, dpJunctions, dpTotal,
      Q_lpm, Q_gpm, K, Le, jData,
    }
  }, [pipeSize, fluidKey, junction, velocity, pipeLen, numJunctions])

  return (
    <div className="grid gap-6 lg:grid-cols-2">

      {/* ── Inputs ── */}
      <div className="space-y-4">
        <Field label="Pipe Nominal Size" hint="Schedule 40, internal diameter">
          <Select<PipeSize> value={pipeSize} onChange={setPipeSize} options={PIPE_OPTIONS} />
        </Field>
        <Field label="Fluid" hint="density & kinematic viscosity preset">
          <Select<FluidKey> value={fluidKey} onChange={setFluidKey} options={FLUID_OPTIONS} />
        </Field>
        <Field label="Junction Type" hint="Crane TP-410 K-factor basis">
          <Select<JunctionKey> value={junction} onChange={setJunction} options={JUNCTION_OPTIONS} />
        </Field>
        <Field label="Flow Velocity" hint="0.1 – 10 m/s">
          <Slider value={velocity} onChange={setVelocity} min={0.1} max={10} step={0.1} unit=" m/s" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Straight Pipe Length" hint="metres">
            <NumberInput value={pipeLen} onChange={setPipeLen} min={0} max={1000} step={1} />
          </Field>
          <Field label="Number of Junctions" hint="in series">
            <NumberInput value={numJunctions} onChange={setNumJunctions} min={1} max={20} step={1} />
          </Field>
        </div>
      </div>

      {/* ── Results ── */}
      <div className="space-y-4">
        {/* Primary outputs */}
        <div className="grid grid-cols-2 gap-3">
          <ResultStat
            label="Total Pressure Drop"
            value={formatPa(result.dpTotal)}
            sub={`${formatBar(result.dpTotal)} · ${formatPsi(result.dpTotal)}`}
            accent="text-cyan-600"
          />
          <ResultStat
            label="Flow Rate"
            value={`${result.Q_lpm.toFixed(1)} L/min`}
            sub={`${result.Q_gpm.toFixed(2)} US gal/min`}
          />
        </div>

        {/* Breakdown table */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Calculation Breakdown
          </div>
          <table className="w-full font-mono text-sm">
            <tbody className="divide-y divide-slate-200">
              <CalcRow label="Pipe ID"              value={`${result.D_mm.toFixed(1)} mm`} />
              <CalcRow label="Reynolds number"      value={result.Re < 1000
                ? result.Re.toFixed(0)
                : result.Re >= 1e6
                  ? `${(result.Re / 1e6).toFixed(2)}M`
                  : `${(result.Re / 1000).toFixed(1)}k`}
              />
              <CalcRow label="Flow regime"          value={result.regime} />
              <CalcRow label="Darcy friction f"     value={result.f.toFixed(5)} />
              <CalcRow label="Dynamic pressure"     value={formatPa(result.dynPress)} />
              <CalcRow label="Pipe run ΔP"          value={formatPa(result.dpStraight)} />
              <CalcRow label={`Junction K (×${numJunctions})`} value={`${result.K} → ${formatPa(result.dpJunctions)}`} />
              <CalcRow label="Equiv. pipe length / junction" value={`${result.Le.toFixed(2)} m`} />
              <CalcRow label="TOTAL ΔP" value={formatPa(result.dpTotal)} strong />
            </tbody>
          </table>
        </div>

        {/* Junction note */}
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <p className="font-mono text-[11px] text-slate-400">{result.jData.note}</p>
        </div>

        <p className="text-xs leading-relaxed text-slate-500">
          Formula:{' '}
          <span className="font-mono text-slate-700">
            ΔP = f·(L/D)·(ρv²/2) + N·K·(ρv²/2)
          </span>
          . Friction factor via Swamee-Jain. K-factors from{' '}
          <span className="font-mono">Crane Technical Paper 410</span>.
        </p>
      </div>
    </div>
  )
}

function CalcRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <tr>
      <td className={`py-1.5 ${strong ? 'font-semibold text-slate-900' : 'text-slate-500'}`}>
        {label}
      </td>
      <td className={`py-1.5 text-right ${strong ? 'font-bold text-cyan-700' : 'text-slate-700'}`}>
        {value}
      </td>
    </tr>
  )
}
